import React from 'react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

interface SubscriptionCancellationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isProcessing: boolean;
    periodEnd?: string | Date;
}

export const SubscriptionCancellationModal: React.FC<SubscriptionCancellationModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    isProcessing,
    periodEnd,
}) => {
    if (!isOpen) return null;

    const formattedDate = periodEnd
        ? new Date(periodEnd).toLocaleDateString(undefined, { dateStyle: 'long' })
        : 'the end of the billing period';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
                {/* Header */}
                <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <ExclamationTriangleIcon className="h-6 w-6 text-amber-500" />
                        Cancel Subscription
                    </h2>
                    <button
                        onClick={onClose}
                        disabled={isProcessing}
                        className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                        aria-label="Close"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    <p className="text-gray-600 mb-4">
                        Are you sure you want to cancel your subscription?
                    </p>

                    <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-6">
                        <p className="text-sm text-amber-800">
                            Your access will continue until <strong>{formattedDate}</strong>. After that, your account will be downgraded to the Free plan.
                        </p>
                    </div>

                    <p className="text-sm text-gray-500">
                        You can resume your subscription at any time before the billing period ends.
                    </p>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3 border-t">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isProcessing}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Keep Subscription
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={isProcessing}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    >
                        {isProcessing && <LoadingSpinner size="sm" />}
                        {isProcessing ? 'Cancelling...' : 'Cancel Subscription'}
                    </button>
                </div>
            </div>
        </div>
    );
};
